import { ArrowOutwardRounded as ArrowOutwardRoundedIcon, AttachMoney, BusinessCenter, Person } from 'components/icons';
import Chip from 'components/@extended/Chip';
import SegmentChip from 'components/@extended/SegmentChip';
import { FormattedDate } from 'components/misc/FormattedDate';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Tooltip } from 'components/ui/tooltip';
import GroupIcon from 'pages/groups/GroupIcon';
import { ReactNode, Ref } from 'react';
import { ClassifierData } from 'types/classifiers';
import { PartyType, RelationshipTypeCode } from 'types/contacts';
import { LawFirmFigures, LawFirmGroupDto, LawFirmListItemDto } from 'types/law-firms';
import { getClassifierText, t } from 'utils/getTranslations';

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit text-[0.875rem] no-underline hover:underline';

/** The contact's name and chips, then whatever the row adds under them. */
export const LawFirmPartyCell = ({
    party,
    classifiers,
    children,
}: {
    party: LawFirmListItemDto['party'];
    classifiers: Record<string, ClassifierData>;
    children?: ReactNode;
}) => (
    <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
            <PartyName party={party} isLink plainLink variant="list" className="font-semibold" />
        </div>
        <div className="flex items-center gap-1">
            {party.partyTypeCode === PartyType.COMPANY ? (
                <Tooltip content={t('contacts.types.company')}>
                    <div>
                        <Chip size="small" label={<BusinessCenter />} type="partyType" />
                    </div>
                </Tooltip>
            ) : (
                <Tooltip content={t('contacts.types.person')}>
                    <div>
                        <Chip size="small" label={<Person />} type="partyType" />
                    </div>
                </Tooltip>
            )}
            {party.relationshipTypeCode === RelationshipTypeCode.CLIENT && (
                <Tooltip content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'CLIENT')}>
                    <div>
                        <Chip size="small" label={<AttachMoney />} type="partyType" />
                    </div>
                </Tooltip>
            )}
            {party.relationshipTypeCode === RelationshipTypeCode.LEAD && (
                <Tooltip content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'LEAD')}>
                    <div>
                        <Chip size="small" label={<ArrowOutwardRoundedIcon />} type="partyType" />
                    </div>
                </Tooltip>
            )}
            {party.segmentTypeCode && <SegmentChip segmentTypeCode={party.segmentTypeCode} />}
            {party.serviceLevelTypeCode && (
                <span className="text-subtitle2 text-grey-600 flex items-center font-semibold">
                    {getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], party.serviceLevelTypeCode)}
                </span>
            )}
        </div>
        {children}
    </div>
);

/** The group's link, and in classic its icon, which the new design's chip draws as its glyph. */
export function GroupLink({ group, linkRef }: { group: LawFirmGroupDto; linkRef?: Ref<HTMLAnchorElement> }) {
    const isNewDesign = useIsNewDesign();
    return (
        <div className="flex items-center gap-1">
            <RecordLink ref={linkRef} className={LINK_CLASS} to={`/group/${group.id}`} label={group.groupName} />
            {isNewDesign ? null : <GroupIcon size="small" />}
        </div>
    );
}

/** The case count and the latest opening; `label` names the count where no column header sits above it. */
export const MattersCell = ({ figures, label }: { figures: LawFirmFigures; label?: string }) => (
    <div className="flex flex-col">
        <p className="text-body1">
            {label && <span className="sr-only">{label}: </span>}
            {figures.relatedMatters?.relatedMatterCount ?? '-'}
        </p>
        {figures.relatedMatters?.lastOpenedDate && (
            <span className="text-caption text-fg-secondary">
                {t('law-firms.last')} {figures.relatedMatters.lastOpenedDate}
            </span>
        )}
    </div>
);

export const ReferralsCell = ({ figures }: { figures: LawFirmFigures }) => (
    <div className="flex flex-col">
        <p className="text-body1">
            {t('law-firms.inbound')}: {figures.inboundReferrals ?? '-'}
        </p>
        <p className="text-body1">
            {t('law-firms.outbound')}: {figures.outboundReferrals ?? '-'}
        </p>
    </div>
);

export const LastReferralCell = ({ figures }: { figures: LawFirmFigures }) => {
    const { lastOutboundReferralDate, lastOutboundReferralForPartyId, lastOutboundReferralForPartyName } = figures;
    if (!lastOutboundReferralDate) return <p className="text-body1">-</p>;
    return (
        <div className="flex flex-col">
            <p className="text-body1">
                <span className="text-fg-secondary">{t('law-firms.bd-effort')}:</span>{' '}
                <FormattedDate value={lastOutboundReferralDate} />
            </p>
            {lastOutboundReferralForPartyName && (
                <p className="text-body1">
                    <span className="text-fg-secondary">{t('law-firms.referral-contact')}:</span>{' '}
                    <PartyName
                        party={{
                            id: lastOutboundReferralForPartyId ?? undefined,
                            name: lastOutboundReferralForPartyName,
                        }}
                        isLink
                        variant="readonly-other"
                    />
                </p>
            )}
        </div>
    );
};
